import { previewNavigationTarget } from "./discovery/discoveryUrl";
/**
 * Opening an App — one rule, every screen.
 *
 * The Instance URL is the App's own address, and it is shareable: every
 * `app_url` anchor in Personal Apps spreads `appLinkProps(url)` so middle-click,
 * ⌘/Ctrl-click and "copy link address" keep working. Deliberately an anchor, not
 * `window.open`: no popup blocker is involved because there is no scripted
 * window to block.
 *
 * Library always opens the saved Instance URL in a new tab. The legacy
 * Instance route resolves an authorized URL and offers that same link; it
 * never embeds saved work. Embedded full apps are public, temporary trials.
 *
 * Share/Invite landing is NOT here: accepting an invite is a different action
 * with its own navigation, owned by that lane.
 */

export const APP_LINK_TARGET = "_blank" as const;

/** Compatibility route for saved Instance bookmarks. It resolves a new-tab
 *  link without starting, stopping or embedding the saved app. */
export function instanceOpenRoute(instanceId: string): string {
  return previewNavigationTarget({ kind: "instance", id: instanceId });
}

/**
 * Adding is separate from launching. Show the saved app's details after Add;
 * Hosted/GPU apps may need placement, and a ready app offers a new-tab link.
 * No asynchronous install opens a popup or embeds saved work.
 */
export function routeAfterAdd(instanceId: string): string {
  return `/?view=discover&detail=${encodeURIComponent(`instance:${instanceId}`)}&tab=app`;
}

/**
 * `noopener` so the opened App cannot reach back at the Ato tab. `noreferrer`
 * is intentionally absent: the App's own origin gains nothing from hiding
 * where its owner came from, and dropping the referrer would make the App's
 * own logs less useful for no safety gain.
 */
export const APP_LINK_REL = "noopener" as const;

export function appLinkProps(url: string): {
  href: string;
  target: typeof APP_LINK_TARGET;
  rel: typeof APP_LINK_REL;
} {
  return { href: url, target: APP_LINK_TARGET, rel: APP_LINK_REL };
}
